import React, { useEffect, useState } from 'react';
import LegalNav from './LegalNav';
import Footer from '../home/Footer';
import Icon from '../Icon';

const formatNum = (i) => String(i + 1).padStart(2, '0');

// Track which section is currently in view so the sidebar TOC can highlight it.
// Uses a deterministic scroll-position detector: the active section is the last
// one whose top has crossed an offset line near the top of the viewport. A
// bottom-of-page fallback ensures the final section gets highlighted even when
// it's too short to reach the reading line.
function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const compute = () => {
      const offset = 140; // reading line ~140px from the top of the viewport
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - offset <= 0) current = id;
        else break;
      }
      // Bottom-of-page fallback — if there's nothing left to scroll, lock to last.
      const docH = document.documentElement.scrollHeight;
      if (window.innerHeight + window.scrollY >= docH - 80) {
        current = ids[ids.length - 1];
      }
      setActive(prev => (prev === current ? prev : current));
    };
    compute();
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join('|')]);
  return active;
}

/**
 * LegalLayout — shared frame for Privacy, Terms, etc.
 *
 * Props:
 *   eyebrowIcon, eyebrowText      — top tag
 *   title, titleEm                — h1: "Privacy" + "<em>Policy</em>"
 *   lede                          — short summary under the title
 *   lastUpdated                   — string shown in the meta pill
 *   intro                         — optional short intro card content
 *   sections                      — [{ id, title, body }]
 */
const LegalLayout = ({
  eyebrowIcon = 'shield',
  eyebrowText = 'Legal',
  title,
  titleEm,
  lede,
  lastUpdated,
  intro,
  sections,
}) => {
  const activeId = useActiveSection(sections.map(s => s.id));
  return (
  <div className="legal-page">
    <LegalNav />

    <section className="legal-hero">
      <div className="container">
        <div className="legal-hero-inner">
          <span className="eyebrow eyebrow-coral">
            <Icon name={eyebrowIcon} size={12} stroke={2.5}/> {eyebrowText}
          </span>
          <h1>{title} <em>{titleEm}</em></h1>
          {lede && <p className="lede">{lede}</p>}
          {lastUpdated && (
            <span className="legal-meta">
              <Icon name="clock" size={13} stroke={2.2}/> Last updated: {lastUpdated}
            </span>
          )}
        </div>
      </div>
    </section>

    <section className="legal-content">
      <div className="container">
        <div className="legal-layout">
          {/* Desktop: sticky sidebar TOC */}
          <aside className="legal-toc" aria-label="Table of contents">
            <div className="legal-toc-label">On this page</div>
            <ol>
              {sections.map((s, i) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className={s.id === activeId ? 'active' : ''}>
                    <span className="num">{formatNum(i)}</span>
                    <span>{s.title}</span>
                  </a>
                </li>
              ))}
            </ol>
          </aside>

          <div className="legal-article">
            {/* Mobile: collapsible TOC */}
            <details className="legal-toc-mobile">
              <summary>On this page · {sections.length} sections</summary>
              <ol>
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`}>
                      <span className="num">{formatNum(i)}</span>
                      <span>{s.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </details>

            {intro && (
              <div className="legal-intro-card">
                <div className="ic"><Icon name="sparkle" size={18} stroke={2.2}/></div>
                <div>{intro}</div>
              </div>
            )}

            {sections.map((s, i) => (
              <article key={s.id} id={s.id} className="legal-section">
                <h2>
                  <span className="num">{formatNum(i)}</span>
                  {s.title}
                </h2>
                {s.body}
              </article>
            ))}

            <div className="legal-help-card">
              <div className="text">
                <h4>Questions about this policy?</h4>
                <p>Our team responds within 24 hours, every day of the week.</p>
              </div>
              <a className="btn btn-primary btn-sm" href="mailto:help@supportcare.io">
                <Icon name="mail" size={14} stroke={2.2}/>
                help@supportcare.io
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
  );
};

export default LegalLayout;
